import { ChangeEvent, useState } from "react";
import { Alert, Col, Form, Row } from "react-bootstrap";
import { StatesAlpha2List } from "../statesAlpha2List";
import { Wohnsitz } from "../AddWohnsitzComponent/AddWohnsitzComponent";
import { FaAsterisk } from "@react-icons/all-files/fa/FaAsterisk";
import moment from "moment";
import { komprimiereBild } from "../bildKompressor";

export type StrafregisterProps = {
    land: string,
    wohnsitze: Wohnsitz[],
    /** Land kommt NUR aus Beschäftigungen (Grenzpendler) — Auszug ist dann
     *  freiwillig, die Behörde verlangt ihn nur für Wohnsitz-Staaten. */
    nurBeschaeftigung?: boolean,
    hasLoadedFile?: boolean,
    loadedFileName?: string
}

export default function StrafregisterInput(props: StrafregisterProps) {

    const [fileInvalidMessage, setFileInvalidMessage] = useState('');
    const [fileInfo, setFileInfo] = useState('');
    const [dateiGewaehlt, setDateiGewaehlt] = useState(false);
    const requiredIcon = <FaAsterisk style={{ color: 'red', paddingLeft: '2px' }} ></FaAsterisk>

    const checkFile = (e: ChangeEvent<HTMLInputElement>) => {
        checkFileElement(e.target);
    }

    // Wie beim Identitätsdokument (App.tsx): zu große Bilder automatisch
    // verkleinern und iPhone-Formate (HEIC) nach JPEG wandeln, statt hart
    // mit „Datei zu groß" abzuweisen (Fall Moroder 03.09.2026).
    const checkFileElement = async (target: HTMLInputElement) => {
        setDateiGewaehlt((target.files?.length ?? 0) > 0);
        if (target.files === null || target.files.length === 0) return;
        const file = target.files[0];
        setFileInfo('');
        const ergebnis = await komprimiereBild(file);
        if (ergebnis.problem) {
            target.setCustomValidity('Datei zu groß');
            setFileInvalidMessage(ergebnis.problem);
            return;
        }
        if (ergebnis.file !== file) {
            const dt = new DataTransfer();
            dt.items.add(ergebnis.file);
            target.files = dt.files;
            setFileInfo(ergebnis.verkleinert
                ? `Foto automatisch verkleinert: ${(file.size / 1e6).toFixed(1)} MB → ${(ergebnis.file.size / 1e6).toFixed(1)} MB`
                : 'Foto automatisch in JPEG umgewandelt');
        }
        target.setCustomValidity('');
        setFileInvalidMessage('');
    }

    const landName = StatesAlpha2List.ALPHA2CODES[props.land] ?? props.land;

    // Sort wohnsitze by von descending, pre-select most recent
    const sortedWohnsitze = [...props.wohnsitze].sort((a, b) => b.von.getTime() - a.von.getTime());
    const defaultWohnsitzIndex = sortedWohnsitze.length > 0 ? 0 : -1;

    // Behörden-Regel (Bartek, 31.08.2026): Beim AKTUELLEN Auslands-Wohnsitz
    // verlangt die Behörde bei JEDEM Antrag einen frischen Auszug (<6 Monate)
    // — der Vorjahres-Auszug reicht dann nicht. Bei früheren Wohnsitzen
    // genügt die einmalige Vorlage (auch ein älterer Auszug).
    const aktuellerWohnsitz = props.wohnsitze.some((w) => !w.bis);
    // Nur-Beschäftigungs-Länder: nichts ist Pflicht. Wird trotzdem eine Datei
    // gewählt, müssen Ausstellungsdatum & Eintragungen dazu erfasst werden.
    const neuerNoetig = props.nurBeschaeftigung
        ? false
        : (aktuellerWohnsitz || !props.hasLoadedFile);
    const felderNoetig = neuerNoetig || (!!props.nurBeschaeftigung && dateiGewaehlt);
    const frischPflicht = aktuellerWohnsitz && !props.nurBeschaeftigung;

    return <>
        <Row className='py-3'>
            <Col xs={12}>
                <h6>Strafregisterauszug {landName}</h6>
                {props.nurBeschaeftigung && (
                    <Alert variant='info' className='mb-2 py-2' style={{ fontSize: '0.85rem' }}>
                        Du hattest in {landName} nur eine <strong>Beschäftigung</strong>, keinen Wohnsitz —
                        ein Strafregisterauszug ist hier <strong>nicht verpflichtend</strong>.
                        Falls du einen hast, kannst du ihn freiwillig mitschicken; das Alter spielt keine Rolle.
                        Du kannst diesen Abschnitt sonst einfach leer lassen.
                    </Alert>
                )}
                {props.land !== 'AT' && !props.nurBeschaeftigung && (
                    <Alert variant='warning' className='mb-2 py-2' style={{ fontSize: '0.85rem' }}>
                        <strong>Wichtig:</strong> Es muss der Auszug aus dem <strong>Strafregister
                        von {landName}</strong> sein — oder eine
                        EU-Strafregisterbescheinigung. Der österreichische Auszug reicht hier nicht.
                        {!props.wohnsitze.some((w) => !w.bis) && (
                            <> Da du dort nicht mehr wohnst, genügt die <strong>einmalige</strong> Vorlage —
                            das Alter des Auszugs spielt dann keine Rolle.</>
                        )}
                    </Alert>
                )}
                {props.land === 'AT' && (
                    <Alert variant='info' className='mb-2 py-2' style={{ fontSize: '0.85rem' }}>
                        <strong>Tipp:</strong> Österreichischer Strafregisterauszug online beantragen:{' '}
                        <a href='https://www.oesterreich.gv.at/themen/dokumente_und_recht/strafregister/Seite.2600200.html' target='_blank' rel='noopener noreferrer'>
                            oesterreich.gv.at
                        </a>
                        <br />
                        Voraussetzungen: ID Austria, Hauptwohnsitz &gt;6 Monate in AT. Gebühr: EUR 10,70 (zur Vorlage bei bestimmter Stelle).
                    </Alert>
                )}
            </Col>
        </Row>
        {aktuellerWohnsitz && props.hasLoadedFile && (
            <Row><Col xs={12}>
                <Alert variant='warning' className='mb-2 py-2' style={{ fontSize: '0.85rem' }}>
                    Dein Auszug vom Vorjahr reicht hier <strong>nicht</strong>: Weil du aktuell in {landName} wohnst,
                    verlangt die Behörde bei jedem Antrag einen <strong>aktuellen</strong> Auszug (höchstens 6 Monate alt).
                </Alert>
            </Col></Row>
        )}
        <Row>
            <Col xs={12} md={6}>
                <Form.Group className='mb-3'>
                    <Form.Label>Datei{neuerNoetig && requiredIcon}</Form.Label>
                    <Form.Control type='file' name={'strafregister_' + props.land} accept='image/*,application/pdf' required={neuerNoetig} onChange={checkFile}></Form.Control>
                    {props.hasLoadedFile && !aktuellerWohnsitz && <Form.Text className="text-success">Vorjahr: {props.loadedFileName || 'geladen'} — einmalige Vorlage genügt für frühere Wohnsitze</Form.Text>}
                    {fileInfo && <Form.Text className="text-success d-block">{fileInfo}</Form.Text>}
                    <Form.Control.Feedback type='invalid' >
                        {fileInvalidMessage}
                    </Form.Control.Feedback>
                </Form.Group>
            </Col>
            <Col xs={12} md={3}>
                <Form.Group className='mb-3'>
                    <Form.Label>Ausstellungsdatum{felderNoetig && requiredIcon}</Form.Label>
                    <Form.Control type='date' name={'strafregister_' + props.land + '_ausstellung'} min={frischPflicht ? moment().add(-6, 'months').format('YYYY-MM-DD') : undefined} max={moment().format('YYYY-MM-DD')} required={felderNoetig}></Form.Control>
                    <Form.Control.Feedback type='invalid'>
                        Ausstellungsdatum darf nicht älter als 6 Monate sein.
                    </Form.Control.Feedback>
                </Form.Group>
            </Col>
            <Col xs={12} md={3}>
                <Form.Group className='mb-3'>
                    <Form.Label>Eintragungen{felderNoetig && requiredIcon}</Form.Label>
                    <Form.Select name={'strafregister_' + props.land + '_eintragungen'} required={felderNoetig}>
                        <option value='nein'>Nein</option>
                        <option value='ja'>Ja</option>
                    </Form.Select>
                </Form.Group>
            </Col>
        </Row>
        {sortedWohnsitze.length > 0 && <Row>
            <Col xs={12} md={6}>
                <Form.Group className='mb-3'>
                    <Form.Label>Zugehöriger Wohnsitz{neuerNoetig && requiredIcon}</Form.Label>
                    <Form.Select name={'strafregister_' + props.land + '_wohnsitz'} defaultValue={defaultWohnsitzIndex.toString()} required={neuerNoetig}>
                        {sortedWohnsitze.map((w, index) =>
                            <option value={index} key={index}>
                                {w.strasse} {w.hausnummer}, {w.plz} {w.ort} ({moment(w.von).format('DD.MM.YYYY')} - {w.bis ? moment(w.bis).format('DD.MM.YYYY') : 'aktuell'})
                            </option>
                        )}
                    </Form.Select>
                </Form.Group>
            </Col>
        </Row>}
    </>
}
